import { isDefined } from '@e-socna/utils';

import { generateId } from '../../../utils/generate-id.util';
import type { MappedReferencesContext } from '../../../contexts/ReferencesContext';

import type { DocumentEditorCmp_TemplateDependency } from '../queries/one-template-dependency.query';
import type { DocumentEditorState } from '../types/editor-state.type';
import type { Editable } from '../types/editable.type';
import type { Getters } from '../types/props.type';
import type { DocumentEditorAction } from '../types/actions.type';
import type { DocumentEditorContentAction } from '../types/content-actions.type';

import { uiReducer } from './domains/ui.reducer';
import { historyReducer } from './domains/history.reducer';
import { contentMutationsReducer } from './domains/content-mutations.reducer';
import { layoutReducer } from './domains/layout.reducer';
import { metadataReducer } from './domains/metadata.reducer';
import { createPushInPast } from './utils/push-in-past.util';
import type { ReducerContext } from './types/reducer-context.type';

interface CreateDocumentEditorReducerParams<E extends Editable> {
  readonly initialEditable: E;
  readonly getters: Getters<E>;
  readonly templateDependencies: readonly DocumentEditorCmp_TemplateDependency[];
  readonly contentOriginsCodeMap: MappedReferencesContext['contentOriginsCodeMap'];
  readonly genericStatuses: MappedReferencesContext['genericStatuses'];
  readonly currentPath: string;
}

const createReducerContext = <E extends Editable>({
  initialEditable,
  templateDependencies,
  contentOriginsCodeMap,
  genericStatuses,
  getters,
  currentPath,
}: Readonly<CreateDocumentEditorReducerParams<E>>): ReducerContext<E> => ({
  initialEditable,
  templateDependencies,
  contentOriginsCodeMap,
  genericStatuses,
  getEditable: getters.editable,
  pushInPast: createPushInPast(currentPath),
  generateId,
});

export const createDocumentEditorReducer = <E extends Editable>({
  initialEditable,
  getters,
  templateDependencies,
  contentOriginsCodeMap,
  genericStatuses,
  currentPath,
}: Readonly<CreateDocumentEditorReducerParams<E>>) => {
  const context = createReducerContext({
    initialEditable,
    templateDependencies,
    contentOriginsCodeMap,
    genericStatuses,
    getters,
    currentPath,
  });

  return (
    state: DocumentEditorState<E>,
    action: DocumentEditorAction<E> | DocumentEditorContentAction,
  ): DocumentEditorState<E> => {
    const reducers = [
      uiReducer,
      historyReducer,
      contentMutationsReducer,
      layoutReducer,
      metadataReducer,
    ];

    for (const reducer of reducers) {
      const result = reducer(state, action, context);
      if (isDefined(result)) {
        return result;
      }
    }

    throw new Error(
      `Unknown action type: ${(action as { type?: string }).type ?? 'undefined'}`,
    );
  };
};
